// Persistent in-editor ad placements — ANONYMOUS users only (the registered
// free tier sees ads at action moments only; paid tiers never load ad code).
// Both placements are normal flow elements (never floating over content — a
// placement-policy requirement) and stay display:none until a unit actually
// fills, so nobody ever sees an empty grey box:
//   - rail:   bottom of the right properties panel
//   - banner: full-width strip between the editor row and the status bar
import { state } from './../state'
import { onAuthChange } from './../auth/supabase'
import { adsConfig } from './config'
import { adFreeUntil } from './gate'
import { provider } from './providers'

// Sizes are pinned (see renderUnit): the host reserves exactly the creative's
// box, so a rendered ad can never be clipped (placement policy).
//   rail:   fixed 200x200 — the largest standard size that fits the 224px panel
//   layers: fixed 200x200 — same, pinned to the 208px layers panel's bottom
//   strip:  widest standard horizontal banner that fits the strip, centered —
//           a "full width" responsive slot gets a left-aligned 970x90 from
//           Google, which reads as half-loaded on wide screens. Never two
//           units side by side (placement policy).
//   mobile: fixed-height 50px banner docked at the bottom of the mobile flow
//           (#mobileEditor is a flex column; appended last = bottom, in flow).
const stripSize = (w: number): { width: number; height: number } =>
  w >= 980 ? { width: 970, height: 90 }
  : w >= 736 ? { width: 728, height: 90 }
  : w >= 476 ? { width: 468, height: 60 }
  : { width: 320, height: 50 }

const PANEL_CSS = 'visibility:hidden;margin-top:auto;padding:8px 0;text-align:center'

const HOSTS: { key: 'slotRail' | 'slotBanner' | 'slotLayers' | 'slotMobile'; id: string; css: string; size: (hostWidth: number) => { width: number; height: number }; mount: () => HTMLElement | null; before?: () => HTMLElement | null }[] = [
  {
    key: 'slotRail',
    id: 'adRail',
    css: PANEL_CSS,
    size: () => ({ width: 200, height: 200 }),
    mount: () => document.getElementById('propertiesPanel'),
  },
  {
    key: 'slotLayers',
    id: 'adLayers',
    css: PANEL_CSS,
    size: () => ({ width: 200, height: 200 }),
    mount: () => document.getElementById('layersPanel'),
  },
  {
    key: 'slotBanner',
    id: 'adStrip',
    css: 'visibility:hidden;width:100%;height:90px;background:#0f172a;text-align:center',
    size: stripSize,
    mount: () => document.getElementById('statusBar')?.parentElement ?? null,
    before: () => document.getElementById('statusBar'),
  },
  {
    key: 'slotMobile',
    id: 'adMobile',
    css: 'visibility:hidden;width:100%;height:50px;background:#0f172a;text-align:center;flex-shrink:0',
    size: () => ({ width: 0, height: 50 }),
    mount: () => document.getElementById('mobileEditor'),
  },
]

function eligible(): boolean {
  const cfg = adsConfig()
  return cfg.enabled && cfg.audience !== 'off' && !!cfg.clientId
    && !state.currentUser && adFreeUntil() <= Date.now()
}

function removeAll(): void {
  for (const h of HOSTS) document.getElementById(h.id)?.remove()
}

/** (Re-)evaluate the persistent placements against the current user/config. */
export function refreshAdPlacements(): void {
  if (!eligible()) { removeAll(); return }
  const cfg = adsConfig()
  for (const h of HOSTS) {
    const slot = cfg[h.key]
    if (!slot || document.getElementById(h.id)) continue
    const parent = h.mount()
    if (!parent) continue
    const host = document.createElement('div')
    host.id = h.id
    host.dataset.testid = h.id
    // visibility:hidden, NOT display:none — AdSense measures available width at
    // push time, and a display:none host measures 0 wide, which kills the fill.
    host.style.cssText = h.css
    const anchor = h.before?.()
    if (anchor) parent.insertBefore(host, anchor)
    else parent.appendChild(host)
    void provider().renderUnit(host, slot, h.size(host.clientWidth)).then((r) => {
      // Unfilled/blocked hosts are removed, not left blank; the modal owns the
      // adblock-wall policy — a passive placement never nags.
      if (r === 'filled' && eligible()) host.style.visibility = 'visible'
      else host.remove()
    }).catch(() => host.remove())
  }
}

/** Boot hook: mount for eligible users now and on every auth change. */
export function initAdPlacements(): void {
  refreshAdPlacements()
  onAuthChange(refreshAdPlacements)
}
